<template>
  <div ref="bannerRef" class="MapCityBanner">
    <div class="banner-text" @click="toggleExpanded">
      {{ localizeWithValues('map.city_banner.label', [cityName]) }}
    </div>

    <SparkMatchCity v-if="isExpanded" :on-change="handleCitySelected" :max-items="3" :key="`map-city-${resetKey}`" />
  </div>
</template>

<script setup lang="ts">
import { localizeWithValues } from '@/core/gambits';
import { useCityStore } from '@/stores/city.store';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import SparkMatchCity from '@/views/main/sparks/components/SparkMatchCity.vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);

const bannerRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const resetKey = ref(0);

const cityName = computed(() => city.value?.name || '');

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const handleCitySelected = async (item: GamDropdownItem | null) => {
  if (!item?.coordinates || item?.id === city.value?.googlePlaceId) {
    return;
  }

  await cityStore.determineCity(item.coordinates);
  isExpanded.value = false;
  resetKey.value++;
};

const handleClickOutside = (event: MouseEvent) => {
  if (!isExpanded.value) return;
  if (bannerRef.value && !bannerRef.value.contains(event.target as Node)) {
    isExpanded.value = false;
  }
};

watch(isExpanded, (expanded) => {
  if (expanded) {
    document.addEventListener('pointerdown', handleClickOutside, true);
  } else {
    document.removeEventListener('pointerdown', handleClickOutside, true);
  }
});

onMounted(() => {
  if (isExpanded.value) {
    document.addEventListener('pointerdown', handleClickOutside, true);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleClickOutside, true);
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.MapCityBanner {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
}

.banner-text {
  background: rgba(220, 38, 38, 0.15);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: var(--radius-small);
  padding: 8px 16px;
  font-size: 13px;
  letter-spacing: 0.03em;
  color: rgb(248, 113, 113);
  white-space: nowrap;
  box-shadow: var(--shadow-medium);
}

.city-name {
  color: rgb(252, 165, 165);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(252, 165, 165, 0.4);
  text-underline-offset: 2px;

  @include hover {
    text-decoration-color: rgb(252, 165, 165);
  }
}
</style>
